import { Alert, Checkbox, DatePicker, Input, Modal } from "antd";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { useCreateSavings2 } from "../../api/api";
import { useGetGroupUsers2 } from "../../api/api.get";
import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";

export default function CreateSavingsDialog({
  open,
  onCancel,
  onSuccess,
}: {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuthContext();
  const { defaultGroupId } = useGroupContext();
  const { mutateAsync, error, isPending } = useCreateSavings2(defaultGroupId);

  const { data: groupUsers } = useGetGroupUsers2(defaultGroupId);

  const { currency } = useCurrencyContext();

  const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]);

  const formik = useFormik({
    initialValues: {
      desc: "",
      date: "",
      amount: 0,
    },
    validationSchema: Yup.object({
      desc: Yup.string().required("Please enter description"),
      date: Yup.string().required("Please select date"),
      amount: Yup.number().required("Please enter number"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        savingsDate: values.date,
        description: values.desc,
        amount: +values.amount,
        users: selectedUsernames,
      }).then(() => {
        onSuccess();
      });
    },
  });

  const userOptions = useMemo(() => {
    if (groupUsers) {
      return groupUsers
        .filter((gu) => gu.username !== user?.username)
        .map((gu) => {
          return { label: gu.firstName, value: gu.username };
        });
    }
    return [];
  }, [groupUsers, user]);

  const handleAddUsers = (usernames: string[]) => {
    setSelectedUsernames(usernames);
  };

  return (
    <form className="space-y-6" onSubmit={formik.handleSubmit}>
      <Modal
        title="Create Savings"
        closable={{ "aria-label": "Custom Close Button" }}
        open={open}
        onOk={formik.submitForm}
        onCancel={onCancel}
        okText="Create"
        okButtonProps={{ loading: isPending }}
      >
        <div className="flex flex-col my-10">
          {error && <Alert type="error" title="Whoops! Please try again" />}
          {/* Savings Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Savings Name *
            </label>
            <Input
              placeholder="e.g., Emergency Fund, Vacation, New Car"
              {...formik.getFieldProps("desc")}
              required
              status={!!formik.errors.desc ? "error" : undefined}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Give your savings goal a meaningful name
            </p>
          </div>

          {/* Date */}
          <div className="my-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Created Date *
            </label>
            <DatePicker
              onChange={(date) =>
                formik.setFieldValue("date", date?.format("YYYY-MM-DD"))
              }
              className={`${formik.values.date ? "" : "border-red-500"}`}
            />

            <p className="text-xs text-muted-foreground mt-1">
              When do you want to add this savings into our system.
            </p>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {currency}
              </span>
              <Input
                prefix={currency}
                type={"number"}
                placeholder="e.g., 100.00"
                {...formik.getFieldProps("amount")}
                status={!!formik.errors.amount ? "error" : undefined}
                required
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Enter the amount
            </p>
          </div>

          {userOptions?.length > 0 && (
            <div className="my-4">
              <p className="text-sm font-semibold">Share With?</p>

              <Checkbox.Group
                options={userOptions}
                onChange={(values: string[]) => handleAddUsers(values)}
              />
            </div>
          )}
        </div>
      </Modal>
    </form>
  );
}
